import { Button } from '@/components/ui/button'
import { TourContent } from '../components/tour-content/tour-content-compound'
import { useTourStore } from '../stores/useTourStore'
import type { TourConfig } from '../types'

const DashboardStepContent = ({
  stepIndex,
  totalSteps,
  isFirst,
  isLast,
  children,
  onBack,
  onContinue,
}: {
  stepIndex: number
  totalSteps: number
  isFirst?: boolean
  isLast?: boolean
  children: React.ReactNode
  onBack?: () => void
  onContinue?: () => void
}) => {
  const { nextStep, prevStep, completeTour } = useTourStore()

  const handleBack = () => {
    if (onBack) {
      onBack()
    } else {
      prevStep()
    }
  }

  const handleContinue = () => {
    if (onContinue) {
      onContinue()
    } else if (isLast) {
      completeTour()
    } else {
      nextStep()
    }
  }

  return (
    <TourContent
      // @ts-ignore - TypeScript cache issue with intent prop
      intent='info'
    >
      {children}
      <TourContent.Footer>
        <div className='flex justify-center mb-4 w-6/12'>
          <div className='flex gap-2 ml-8 mr-12 mt-4'>
            {Array.from({ length: totalSteps }, (_, i) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: Steps are static and order never changes
                key={i}
                className={`w-2 h-2 rounded-full ${i === stepIndex ? 'bg-primary-500' : 'bg-gray-300'}`}
              />
            ))}
          </div>
        </div>
        <TourContent.Actions alignment='between'>
          <Button
            // @ts-ignore - TypeScript cache issue with variant prop
            variant='outline'
            onClick={handleBack}
          >
            Back
          </Button>
          <Button onClick={handleContinue}>Continue</Button>
        </TourContent.Actions>
      </TourContent.Footer>
    </TourContent>
  )
}

export const dashboardTour: TourConfig = {
  id: 'dashboard',
  name: 'Dashboard Tour',
  initialRoute: '/companies', // Will match /companies/[id] routes
  steps: [
    {
      target: '#sidebar-dashboard',
      content: (
        <DashboardStepContent stepIndex={1} totalSteps={6}>
          <TourContent.Header>
            <TourContent.Title>Explore Your Dashboard</TourContent.Title>
          </TourContent.Header>
          <TourContent.Body>
            <TourContent.Description>
              The dashboard shows your team's activity and published content. If you don't see anything yet, your
              manager might still be setting things up.
            </TourContent.Description>
          </TourContent.Body>
        </DashboardStepContent>
      ),
      placement: 'right',
      disableBeacon: true,
      spotlightClicks: true,
      disableOverlay: false,
      hideCloseButton: true,
      hideFooter: true,
      waitForTarget: false,
      styles: {
        spotlight: {
          display: 'none',
        },
      },
    },
    {
      target: '#sidebar-posts',
      content: (
        <DashboardStepContent stepIndex={2} totalSteps={6}>
          <TourContent.Header>
            <TourContent.Title>Collaborate with Content</TourContent.Title>
          </TourContent.Header>
          <TourContent.Body>
            <TourContent.Description>
              Create and manage posts directly here. You can also view and reply to comments from credentialed sports
              media in real time.
            </TourContent.Description>
          </TourContent.Body>
        </DashboardStepContent>
      ),
      placement: 'right',
      disableBeacon: true,
      spotlightClicks: true,
      disableOverlay: false,
      hideCloseButton: true,
      hideFooter: true,
      waitForTarget: false,
      styles: {
        spotlight: {
          display: 'none',
        },
      },
    },
    {
      target: '#sidebar-companies',
      content: (
        <DashboardStepContent stepIndex={3} totalSteps={6}>
          <TourContent.Header>
            <TourContent.Title>Team Overview</TourContent.Title>
          </TourContent.Header>
          <TourContent.Body>
            <TourContent.Description>
              Here is where you can find general information about your team.
            </TourContent.Description>
          </TourContent.Body>
        </DashboardStepContent>
      ),
      placement: 'right',
      disableBeacon: true,
      spotlightClicks: true,
      disableOverlay: false,
      hideCloseButton: true,
      hideFooter: true,
      waitForTarget: false,
      styles: {
        spotlight: {
          display: 'none',
        },
      },
    },
    {
      target: '#sidebar-users',
      content: (
        <DashboardStepContent stepIndex={4} totalSteps={6}>
          <TourContent.Header>
            <TourContent.Title>Who's on the team</TourContent.Title>
          </TourContent.Header>
          <TourContent.Body>
            <TourContent.Description>Manage and view your team members here</TourContent.Description>
          </TourContent.Body>
        </DashboardStepContent>
      ),
      placement: 'right',
      disableBeacon: true,
      spotlightClicks: true,
      disableOverlay: false,
      hideCloseButton: true,
      hideFooter: true,
      waitForTarget: false,
      styles: {
        spotlight: {
          display: 'none',
        },
      },
    },
    {
      target: '#sidebar-media-members',
      content: (
        <DashboardStepContent stepIndex={5} totalSteps={6}>
          <TourContent.Header>
            <TourContent.Title>Who's receiving our content</TourContent.Title>
          </TourContent.Header>
          <TourContent.Body>
            <TourContent.Description>
              See which members of the sports media your team has accepted to view your content. Also, check here for
              any new follow requests from media members.
            </TourContent.Description>
          </TourContent.Body>
        </DashboardStepContent>
      ),
      placement: 'right',
      disableBeacon: true,
      spotlightClicks: true,
      disableOverlay: false,
      hideCloseButton: true,
      hideFooter: true,
      waitForTarget: false,
      styles: {
        spotlight: {
          display: 'none',
        },
      },
    },

    {
      target: '#notification-bell',
      content: (
        <DashboardStepContent stepIndex={6} totalSteps={6} isLast>
          <TourContent.Header>
            <TourContent.Title>Stay in the Loop</TourContent.Title>
          </TourContent.Header>
          <TourContent.Body>
            <TourContent.Description>
              Keep an eye on your notifications to stay updated when new content is shared or your team interacts.
              You're now ready to collaborate!
            </TourContent.Description>
          </TourContent.Body>
        </DashboardStepContent>
      ),
      placement: 'bottom',
      disableBeacon: true,
      spotlightClicks: true,
      disableOverlay: false,
      hideCloseButton: true,
      hideFooter: true,
      waitForTarget: false,
    },
  ],
}
